import { readContract, readContracts } from '@wagmi/core';
import { useAccount, usePublicClient, useReadContracts } from 'wagmi';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { erc20Abi, /*, parseUnits*/ formatUnits, isAddress } from 'viem';
import { Args, Web3Button/*, estimateAndSend*/ } from './Web3Button';
import { MasterWizardAbi, IRehypothecationProtocolAbi, IUniswapV2PairAbi, PulseXv1MasterChefAbi } from './abi/abis';
import { config } from './Web3ModalProvider';
import { LiquidLoansStabilityPoolAbi } from './abi/LiquidLoans';
import { LiquityStabilityPoolAbi } from './abi/Liquity';

type Address = `0x${string}`;
const ZeroAddress = '0x0000000000000000000000000000000000000000';

const masterChefContractAddress: Address = '0x0c22e23040c27ea3cF0EbF14B0ff1F9f5e0d0B56';
const rewardTokenAddress: Address = '0x6b8840222130801c45f3e388eE47bb5EFC52B7aE';
const rewardTokenSymbol = 'DAT';
const protocolName = "MasterChef";

const devAddress = '0x0000000000000000000000000000000000000000';
const feeAddress = '0xDc1a7CB9D4184565564A07E4A4c47326c4AF1160';

const rewardToken1Address = '0x8b4cfb020aF9AcAd95AD80020cE8f67FBB2C700E';
const rewardToken1Symbol = "BBC";
const rewardToken2Address = undefined;
const rewardToken2Symbol = "";
const rewardToken3Address = undefined;
const rewardToken3Symbol = '';
const rewardToken4Address = undefined;
const rewardToken4Symbol = '';

type PoolInfo = {
  tokenAddress: Address;
  allocPoint: bigint;
  lastRewardBlock: bigint;
  accRewardTokensPerShare: bigint;
  depositFeeBP: bigint;
  withdrawFeeBP: bigint;
};

type AmountProps = {
  amount: bigint | undefined;
  displayDecimals?: bigint;
  // isLoading?: boolean;
}
function Amount({ amount, displayDecimals = 4n }: AmountProps) {
  if (typeof amount == "bigint") {
    const precision = 10n ** (18n - displayDecimals);
    amount = amount / precision * precision;
  }
  return <>
    {typeof amount == "bigint" ? formatUnits(amount, 18) : "N/A"}
  </>;
}

type PoolPanelProps = {
  masterChefContractAddress: Address;
  pid?: number,
  poolInfo?: PoolInfo;
  poolInfoRefetch: () => void;
  revision: number;
};
function PoolPanel({ masterChefContractAddress, pid, poolInfo, poolInfoRefetch, revision }: PoolPanelProps) {
  const addMode = pid === undefined;
  const showDetails = false;
  const pidBI = (pid && pid >= 0) ? BigInt(pid) : 999999999n;
  let { tokenAddress, allocPoint, lastRewardBlock, accRewardTokensPerShare, depositFeeBP, withdrawFeeBP } = poolInfo ?? {};

  const { data: tokenInfo, isLoading: tokenInfoIsLoading } = useReadContracts({
    allowFailure: true,
    scopeKey: `tokenInfo-${revision}`,
    contracts: [
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'symbol',
        args: [],
      },
      {
        address: tokenAddress,
        abi: IUniswapV2PairAbi,
        functionName: 'token0',
        args: [],
      },
      {
        address: tokenAddress,
        abi: IUniswapV2PairAbi,
        functionName: 'token1',
        args: [],
      },
      {
        address: masterChefContractAddress,
        abi: MasterWizardAbi,
        functionName: 'rehypothecations',
        args: [pidBI],
      },
      {
        address: masterChefContractAddress,
        abi: MasterWizardAbi,
        functionName: 'getTotalDepositAmount',
        args: [pidBI],
      },
    ],
    // enabled: !addMode && !!tokenAddress, // FIXME
  });

  const tokenName = tokenInfo?.[0]?.result;
  const tokenLPToken0 = tokenInfo?.[1]?.result as Address | undefined;
  const tokenLPToken1 = tokenInfo?.[2]?.result as Address | undefined;
  const rhypoContractAddress = tokenInfo?.[3]?.result as Address | undefined;
  const stakedAmount = tokenInfo?.[4]?.result as bigint | undefined;

  const { data: lpTokenInfo, isLoading: lpTokenInfoIsLoading } = useReadContracts({
    allowFailure: true,
    scopeKey: `lpTokenInfo-${revision}`,
    contracts: [
      {
        address: tokenLPToken0,
        abi: erc20Abi,
        functionName: 'symbol',
        args: [],
      },
      {
        address: tokenLPToken1,
        abi: erc20Abi,
        functionName: 'symbol',
        args: [],
      },
    ],
    // enabled: !addMode && !!tokenLPToken0 && !!tokenLPToken1, // FIXME
  });

  const { data: rhypoInfo, isLoading: rhypoInfoIsLoading } = useReadContracts({
    allowFailure: true,
    scopeKey: `rhypoInfo-${revision}`,
    contracts: [
      {
        address: rhypoContractAddress,
        abi: IRehypothecationProtocolAbi,
        functionName: 'getTotalDepositAmount',
        args: [],
      },
      {
        address: rhypoContractAddress,
        abi: IRehypothecationProtocolAbi,
        functionName: 'name',
        args: [],
      },
    ],
    // enabled: !addMode, // FIXME
  });

  console.log(pid, "rhypoInfo", rhypoContractAddress, rhypoInfo);

  const [rhypoHasRewards, setRhypoHasRewards] = useState<undefined | boolean>();
  useEffect(() => {
    async function read() {
      if (!rhypoContractAddress)
        return;

      try {
        const result = await readContract(config, {
          address: rhypoContractAddress,
          account: masterChefContractAddress,
          abi: IRehypothecationProtocolAbi,
          functionName: 'claimRewards',
          args: [feeAddress],
        });
        setRhypoHasRewards(result as boolean);
      }
      catch (err) {
        setRhypoHasRewards(false);
      }
    }
    read();
  }, [masterChefContractAddress, rhypoContractAddress, revision]);

  const tokenLPToken0Symbol = lpTokenInfo?.[0]?.result as string | undefined; // FIXME
  const tokenLPToken1Symbol = lpTokenInfo?.[1]?.result as string | undefined; // FIXME
  const rhypoTotalDepositAmount = rhypoInfo?.[0]?.result as bigint | undefined; // FIXME
  const rhypoName = rhypoInfo?.[1]?.result as string | undefined; // FIXME

  const [rhypoDetails, setRhypoDetails] = useState<undefined | string>();
  useEffect(() => {
    async function read() {
      if (!rhypoContractAddress || !rhypoName)
        return;

      try {
        setRhypoDetails(await queryRhypoDetails(rhypoName, rhypoContractAddress));
      }
      catch (err: any) {
        setRhypoDetails(err.shortMessage ?? `${err}`);
      }

    }
    read();
  }, [masterChefContractAddress, rhypoName, rhypoContractAddress, revision]);

  const [newTokenAddress, setNewTokenAddress] = useState(addMode ? "0x" : tokenAddress);
  const [newAllocPoint, setNewAllocPoint] = useState(addMode ? 1 : allocPoint);
  const [newDepositFeeBP, setNewDepositFeeBP] = useState(addMode ? 0 : depositFeeBP);
  const [newWithdrawFeeBP, setNewWithdrawFeeBP] = useState(addMode ? 0 : withdrawFeeBP);
  const [newRhypoContractAddress, setNewRhypoContractAddress] = useState(addMode ? ZeroAddress : '');

  useEffect(() => {
    if (rhypoContractAddress && newRhypoContractAddress === '')
      setNewRhypoContractAddress(rhypoContractAddress);
  }, [rhypoContractAddress, newRhypoContractAddress]);

  return <div className={`border-2 border-green-900 bg-gradient-to-b from-green-950 p-3 rounded-xl${allocPoint === 0n ? ' bg-gray-700' : ''}`}>
    <div className='grid grid-cols-[auto_auto] gap-2 items-center'>
      <div className='col-span-2 flex flex-row'>
        <div className='label text-bold text-xl grow'>
          Pool #{`${pid}`}{' '}
          {addMode ?
            <span className='text-blue-500'>New</span>
            :
            <>
              {allocPoint === 0n ? <span className='text-orange-500'>{' '}INACTIVE</span> : ''}
              {tokenInfoIsLoading || lpTokenInfoIsLoading || rhypoInfoIsLoading ?
                <div>...</div>
                : <>
                  {' '}{tokenName ?? "No name?"}
                  {tokenLPToken0Symbol &&
                    <>{' '}{tokenLPToken0Symbol}/{tokenLPToken1Symbol}</>
                  }
                </>
              }
            </>
          }
        </div>
        <div className='col-span-2 flex flex-row justify-end'>
          {addMode ? <AddPoolButton /> : <SetPoolPropertiesButton />}
        </div>
      </div>

      <div className='label'>
        Staked token address:
      </div>
      <input className='input w-[25rem]' disabled={!addMode} type="text" min="0" value={newTokenAddress} onChange={ev => setNewTokenAddress(ev.target.value as Address)} />

      <div className='label'>
        Allocation points:
      </div>
      <div className='value flex flex-row gap-2 items-center'>
        <input className='input text-center w-24' type="number" min="0" value={Number(newAllocPoint)} onChange={ev => setNewAllocPoint(parseFloat(ev.target.value))} />
        <span>[100 = 1X multiplier]</span>
      </div>

      <div className='label'>
        Deposit/withdraw fee:
      </div>
      <div className='value flex flex-row gap-2 items-center'>
        <input className='input text-center w-16' type="number" min="0" value={`${newDepositFeeBP}`} onChange={ev => setNewDepositFeeBP(parseFloat(ev.target.value))} />
        <span>/</span>
        <input className='input text-center w-16' type="number" min="0" value={`${newWithdrawFeeBP}`} onChange={ev => setNewWithdrawFeeBP(parseFloat(ev.target.value))} />
        <span>[1/100 of %]</span>
      </div>

      {!addMode && <>
        <div className='label'>
          Last reward block:
        </div>
        <div className='value'>
          {lastRewardBlock ? Number(lastRewardBlock) : '-'}
        </div>

        {showDetails &&
          <div className='col-span-2 flex flex-row items-center gap-2'>
            <div className='label'>
              Acc. tokens per share: <Amount amount={accRewardTokensPerShare} />
            </div>
            {' '}
            <div className='value'>Staked amount: <Amount amount={stakedAmount} displayDecimals={6n} /></div>
          </div>
        }
      </>}

        
      {(!addMode) && <>
        <div className='label'>
          Rehypothecation:
        </div>
        <div className='flex flex-col gap-2'>
          {rhypoContractAddress !== ZeroAddress ? <div>{rhypoName ?? "(no name?)"}</div> : <div>None</div>}
          {(stakedAmount === 0n || rhypoContractAddress !== ZeroAddress) ?
            <div className='flex flex-row items-center gap-2'>
              <input className='input w-[25rem]' disabled={stakedAmount !== 0n} type="text" min="0" value={newRhypoContractAddress} onChange={ev => setNewRhypoContractAddress(ev.target.value)} />
              <SetRehypothecationButton disabled={stakedAmount !== 0n || !isAddress(newRhypoContractAddress) || newRhypoContractAddress === ZeroAddress} />
            </div>
            :
            <>{showDetails && <code>{rhypoContractAddress}</code>}</>
          }
          {(!addMode && !!rhypoContractAddress && rhypoContractAddress !== ZeroAddress) && <>
            <div>Total deposit: <Amount amount={rhypoTotalDepositAmount} displayDecimals={6n} /></div>
            {/* <div>Rewards: {rhypoHasRewards ? "yes" : "no"}</div> */}
            <div className='flex flex-row gap-2 items-center'>
              <ClaimButton disabled={!rhypoHasRewards} />
              <EnableEmergencyWithdrawButton />
            </div>
          </>
          }
          {rhypoDetails && <div>{rhypoDetails}</div>}
          </div>
      </>}
    </div>
  </div>;

  function AddPoolButton() {
    return <Web3Button
      className="w-24"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const args = [
          newAllocPoint,
          newTokenAddress,
          newDepositFeeBP ?? depositFeeBP,
          newWithdrawFeeBP ?? withdrawFeeBP,
          true, // _withUpdate
          true, // _checkDuplicate
        ];
        console.log(protocolName + ".add", args);
        const promise = estimateAndSend("add", args);
        toast.promise(promise, { pending: `Adding new pool...` });
        return await promise;
      }}
      // disabled={!account?.address || !(userBalance > 0n)}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to add pool: ${err.shortMessage ?? err}`, { delay: 10 });
        poolInfoRefetch();
      }}
      onSuccess={async () => {
        toast(`New pool added`);
        poolInfoRefetch();
      }}
    >
      Add
    </Web3Button>;
  }

  function SetPoolPropertiesButton() {
    return <Web3Button
      className="w-24"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const args = [
          pid,
          newAllocPoint ?? allocPoint,
          newDepositFeeBP ?? depositFeeBP,
          newWithdrawFeeBP ?? withdrawFeeBP,
          true,
        ];
        console.log(protocolName + ".set", args);
        const promise = estimateAndSend("set", args);
        toast.promise(promise, { pending: `Changing attributes of pool #${pid}...` });
        return await promise;
      }}
      // disabled={!account?.address || !(userBalance > 0n)}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to set pool #${pid} attributes: ${err.shortMessage ?? err}`, { delay: 10 });
        poolInfoRefetch();
      }}
      onSuccess={async () => {
        toast(`Pool #${pid} attributes set`);
        poolInfoRefetch();
      }}
    >
      Set
    </Web3Button>;
  }

  type ClaimButtonProps = {
    disabled: boolean;
  }
  function ClaimButton({ disabled }: ClaimButtonProps) {
    return <Web3Button
      className="w-32"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      disabled={disabled}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const args = [
          pid
        ];
        console.log(protocolName + ".claimRehypothecationRewards", args);
        const promise = estimateAndSend("claimRehypothecationRewards", args);
        toast.promise(promise, { pending: `Claiming rehypothecation rewards for pool #${pid}...` });
        return await promise;
      }}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to claim rehypothecation rewards for pool #${pid}: ${err.shortMessage ?? err}`, { delay: 10 });
        // incInfoRefetch();
      }}
      onSuccess={async () => {
        toast(`Rehypothecation rewards #${pid} claimed`);
        // incInfoRefetch();
      }}
    >
      Claim
    </Web3Button>;
  }

  type SetRehypothecationButtonProps = {
    disabled: boolean;
  }
  function SetRehypothecationButton({ disabled }: SetRehypothecationButtonProps) {
    return <Web3Button
      className="w-14"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      disabled={disabled}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const args = [
          pid,
          newRhypoContractAddress,
        ];
        console.log(protocolName + ".setRehypothecation", args);
        const promise = estimateAndSend("setRehypothecation", args);
        toast.promise(promise, { pending: `Setting rehypothecation protocol contract #${pid}...` });
        return await promise;
      }}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to set rehypothecation protocol contract for pool #${pid}: ${err.shortMessage ?? err}`, { delay: 10 });
        // incInfoRefetch();
      }}
      onSuccess={async () => {
        toast(`Rehypothecation protocol contract #${pid} changed`);
        // incInfoRefetch();
      }}
    >
      Set
    </Web3Button>;
  }
  function EnableEmergencyWithdrawButton() {
    return <Web3Button
      className="w-32"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const args = [
          pid
        ];
        console.log(protocolName + ".enableEmergencyWithdraw", args);
        const promise = estimateAndSend("enableEmergencyWithdraw", args);
        toast.promise(promise, { pending: `Enabling emergency withdraw for pool #${pid}...` });
        return await promise;
      }}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to enabled emergency claim for pool #${pid}: ${err.shortMessage ?? err}`, { delay: 10 });
      }}
      onSuccess={async () => {
        toast(`Emergency claim for pool #${pid} enabled`);
      }}
    >
      Enable 🚑💰
    </Web3Button>;
  }
}

async function queryRhypoDetails(rhypoName: string, rhypoContractAddress: Address): Promise<string> {
  if (rhypoName.includes("MasterChef-PulseXv1")) {
    let pid = 99999n;
    if (rhypoName.includes("WPLS/DAI")) pid = 1n;
    if (rhypoName.includes("WPLS/USDC")) pid = 2n;
    if (rhypoName.includes("WPLS/USDT")) pid = 5n;
    const result = await readContract(config, {
      address: '0xB2Ca4A66d3e57a5a9A12043B6bAD28249fE302d4',
      abi: PulseXv1MasterChefAbi,
      functionName: 'pendingInc',
      args: [pid, rhypoContractAddress],
    });
    return `#${pid} Pending ${Math.ceil(Number(result) / 1e18 * 10000) / 10000} INC`;
  }

  if (rhypoName === protocolName + ".LiquidLoans.StabilityPool") {
    const result = await readContracts(config, {
      contracts: [
        {
          address: '0x7bFD406632483ad00c6EdF655E04De91A96f84bc',
          abi: LiquidLoansStabilityPoolAbi,
          functionName: 'getDepositorLOANGain',
          args: [rhypoContractAddress],
        },
        {
          address: '0x7bFD406632483ad00c6EdF655E04De91A96f84bc',
          abi: LiquidLoansStabilityPoolAbi,
          functionName: 'getDepositorPLSGain',
          args: [rhypoContractAddress],
        },
      ]
    });
    return `Pending ${Math.ceil(Number(result[0].result) / 1e18)} LOAN, ${Math.ceil(Number(result[1].result) / 1e18)} PLS`;
  }

  if (rhypoName === protocolName + ".PowerCityEarn.StabilityPool"
    || rhypoName === protocolName + ".PowerCityFlex.StabilityPool"
  ) {
    const isEARN = rhypoName === protocolName + ".PowerCityEarn.StabilityPool";
    const address = isEARN ? '0x02E842db8d6C78D17cF8146009Fb864094d95319' : '0x271F576fD6DE465231320a0F9997ACb0C8b97e07';
    const token1 = isEARN ? 'EARN' : 'FLEX';
    const token2 = isEARN ? 'PLSX' : 'HEX';
    const result = await readContracts(config, {
      contracts: [
        {
          address,
          abi: LiquityStabilityPoolAbi,
          functionName: 'getDepositorLQTYGain',
          args: [rhypoContractAddress],
        },
        {
          address,
          abi: LiquityStabilityPoolAbi,
          functionName: 'getDepositorETHGain',
          args: [rhypoContractAddress],
        },
      ]
    });
    return `Pending ${Math.ceil(Number(result[0].result) / 1e18 * 1e6) / 1e6} ${token1}, ${Math.ceil(Number(result[1].result) / 1e6 * 1e18) / 1e6} ${token2}`;
  }

  return "";
}

function ClaimAllButton() {
  return <Web3Button
    className="w-32"
    contractAddress={masterChefContractAddress}
    contractAbi={MasterWizardAbi}
    action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
      const args: Args = [];
      console.log(protocolName + ".claimAllRehypothecationRewards", args);
      const promise = estimateAndSend("claimAllRehypothecationRewards", args);
      toast.promise(promise, { pending: `Claiming all rehypothecation rewards...` });
      return await promise;
    }}
    onError={async (err: any) => {
      console.log(err);
      toast.error(`Failed to claim all rehypothecation rewards: ${err.shortMessage ?? err}`, { delay: 10 });
    }}
    onSuccess={async () => {
      toast(`Claiming all rehypothecation rewards succeed`);
    }}
  >
    Claim all
  </Web3Button>;
}

export default function Home() {
  const account = useAccount();
  const publicClient = usePublicClient();

  const [poolsIsFetching, setPoolsIsFetching] = useState(false);
  const [poolsRevision, setPoolsRevision] = useState(0);
  const [pools, setPools] = useState<PoolInfo[]>([]);

  const { data: globalInfo/*, isLoading: globalInfoIsLoading*/, refetch: globalInfoRefetch } = useReadContracts({
    allowFailure: true,
    contracts: [
      {
        address: masterChefContractAddress,
        abi: MasterWizardAbi,
        functionName: 'rewardTokenAmountPerBlock',
        args: [],
      },
      {
        address: rewardTokenAddress ?? ZeroAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [devAddress],
      },
      {
        address: rewardToken1Address ?? ZeroAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [feeAddress],
      },
      {
        address: rewardToken2Address ?? ZeroAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [feeAddress],
      },
      {
        address: rewardToken3Address ?? ZeroAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [feeAddress],
      },
      {
        address: rewardToken4Address ?? ZeroAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [feeAddress],
      },
    ],
  });

  const emission = globalInfo?.[0]?.result as bigint | undefined; // FIXME
  const devAddressRewardAmount = globalInfo?.[1]?.result as bigint | undefined; // FIXME
  const feeAddressRewardToken1Amount = globalInfo?.[2]?.result as bigint | undefined; // FIXME
  const feeAddressRewardToken2Amount = globalInfo?.[3]?.result as bigint | undefined; // FIXME
  const feeAddressRewardToken3Amount = globalInfo?.[4]?.result as bigint | undefined; // FIXME
  const feeAddressRewardToken4Amount = globalInfo?.[5]?.result as bigint | undefined; // FIXME

  useEffect(() => {
    async function fetch() {
      if (!publicClient)
        return;
      setPoolsIsFetching(true);
      try {
        const poolLength = await publicClient.readContract({
          address: masterChefContractAddress,
          abi: MasterWizardAbi,
          functionName: 'poolLength',
        }) as bigint | undefined; // FIXME:

        if (poolLength && poolLength > 0n) {
          type Call = {
            address: Address,
            abi: typeof MasterWizardAbi,
            functionName: string,
            args: Args,
          }
          const calls: Call[] = [];
          for (let pid = 0n; pid < poolLength; pid += 1n) {
            calls.push({
              address: masterChefContractAddress,
              abi: MasterWizardAbi,
              functionName: 'poolInfo',
              args: [pid],
            });
          };

          const poolData = await readContracts(config, {
            contracts: calls,
            allowFailure: true,
          });

          const poolDataResults = poolData.map(_ => {
            const result = _.result as unknown as (Address | string | bigint)[];
            return {
              tokenAddress: (result?.[0]) as Address,
              allocPoint: result?.[1] as bigint,
              lastRewardBlock: result?.[2] as bigint,
              accRewardTokensPerShare: result?.[3] as bigint,
              depositFeeBP: result?.[4] as bigint,
              withdrawFeeBP: result?.[5] as bigint,
            };
          });
          setPools(poolDataResults);
        }
        else {
          setPools([]);
        }
      }
      finally {
        setPoolsIsFetching(false);
      }
    }
    fetch();
  }, [account?.isConnected, publicClient, poolsRevision]);

  function poolInfoRefetch() {
    globalInfoRefetch();
    console.log("Pool update requested");
    setPoolsRevision(prev => prev + 1);
  }

  const [newEmission, setNewEmission] = useState<string>('');
  useEffect(() => {
    if (newEmission === '' && emission !== undefined)
      setNewEmission(`${Number(emission) / 1e18}`);
  }, [emission, newEmission]);

  function UpdateEmissionRateButton() {
    return <Web3Button
      className="w-24 !inline"
      contractAddress={masterChefContractAddress}
      contractAbi={MasterWizardAbi}
      action={async ({ estimateAndSend/*, publicClient, walletClient*/ }) => {
        const amount = BigInt(Math.round(parseFloat(newEmission!) * 1e18));
        if (amount <= 0n) {
          throw new Error(`Invalid emission value '${newEmission}'`);
        }
        const args: Args = [amount];
        console.log("MasterWizard.updateEmissionRate", args);
        const promise = estimateAndSend("updateEmissionRate", args);
        toast.promise(promise, { pending: `Changing emission rate...` });
        return await promise;
      }}
      onError={async (err: any) => {
        console.log(err);
        toast.error(`Failed to change emission rate: ${err.shortMessage ?? err}`, { delay: 10 });
        globalInfoRefetch();
      }}
      onSuccess={async () => {
        toast(`Changing emission rate succeed`);
        globalInfoRefetch();
      }}
    >
      Change
    </Web3Button>;
  };

  return (
    account?.isConnected ?
      <div className='flex flex-col gap-3 w-full'>
        <div className='flex flex-row gap-2 items-center'>
          <div className='grow'>
            Dev: <Amount amount={devAddressRewardAmount} displayDecimals={4n} /> {rewardTokenSymbol}
            {' | '}Fee:
            {rewardToken1Address && <>
              <Amount amount={feeAddressRewardToken1Amount} displayDecimals={4n} /> {rewardToken1Symbol}
            </>}
            {rewardToken2Address && <>
              {' - '}<Amount amount={feeAddressRewardToken2Amount} displayDecimals={4n} /> {rewardToken2Symbol}
            </>}
            {rewardToken3Address && <>
              {' - '}<Amount amount={feeAddressRewardToken3Amount} displayDecimals={4n} /> {rewardToken3Symbol}
            </>}
            {rewardToken4Address && <>
              {' - '}<Amount amount={feeAddressRewardToken4Amount} displayDecimals={4n} /> {rewardToken4Symbol}
            </>}
            {' | '}Emission:
            {' '}<input className='input text-right w-[5rem]' type="number" value={newEmission} onChange={ev => setNewEmission(ev.target.value)}></input> {rewardTokenSymbol}/block
            {' '}<UpdateEmissionRateButton />
          </div>
          <ClaimAllButton />
          <button className={`btn w-24${poolsIsFetching ? ' bg-animate-barberpole' : ''}`} onClick={poolInfoRefetch}>Refresh</button>
        </div>
        <div className='grid grid-cols-fluid gap-3 w-full'>
          {pools.map((poolInfo, pid) => {
            // if(pid !== 9 && pid != 8 && pid != 10) return <></>;
            return <PoolPanel
              pid={pid}
              key={pid}
              poolInfo={poolInfo}
              masterChefContractAddress={masterChefContractAddress}
              poolInfoRefetch={poolInfoRefetch}
              revision={poolsRevision}
            />
          })}

          <PoolPanel
            masterChefContractAddress={masterChefContractAddress}
            poolInfoRefetch={poolInfoRefetch}
            revision={poolsRevision}
          />
        </div>

      </div >
      :
      <div className='btn !bg-red-600'>Please connect your wallet first.</div>
  );
}
